import React, { useState } from 'react';
import { FaUsers, FaFileAlt, FaChartBar, FaCog, FaEye, FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import AdminLayout from '../components/admin/AdminLayout';

const AdminDashboard: React.FC = () => {
  const [language, setLanguage] = useState<'ar' | 'en'>('ar');

  const isRTL = language === 'ar';

  const stats = [
    { 
      title: isRTL ? 'إجمالي الصفحات' : 'Total Pages', 
      value: '8', 
      icon: FaFileAlt, 
      color: 'bg-blue-500',
      change: '+2'
    },
    { 
      title: isRTL ? 'السكاشن النشطة' : 'Active Sections', 
      value: '24', 
      icon: FaEdit, 
      color: 'bg-green-500',
      change: '+5'
    },
    { 
      title: isRTL ? 'المشاهدات اليوم' : 'Today Views', 
      value: '1,234', 
      icon: FaEye, 
      color: 'bg-purple-500',
      change: '+12%'
    },
    { 
      title: isRTL ? 'المستخدمين النشطين' : 'Active Users', 
      value: '89', 
      icon: FaUsers, 
      color: 'bg-orange-500',
      change: '+8%'
    }
  ];

  const recentPages = [
    { id: 'home', title: isRTL ? 'الصفحة الرئيسية' : 'Home Page', lastModified: '2024-01-15', status: 'active' },
    { id: 'about', title: isRTL ? 'من نحن' : 'About Us', lastModified: '2024-01-14', status: 'active' },
    { id: 'services', title: isRTL ? 'الخدمات' : 'Services', lastModified: '2024-01-13', status: 'active' },
    { id: 'projects', title: isRTL ? 'المشاريع' : 'Projects', lastModified: '2024-01-12', status: 'active' },
    { id: 'contact', title: isRTL ? 'اتصل بنا' : 'Contact', lastModified: '2024-01-11', status: 'active' }
  ];

  const quickActions = [
    { 
      title: isRTL ? 'إضافة صفحة جديدة' : 'Add New Page', 
      icon: FaPlus, 
      color: 'bg-blue-600',
      href: '/admin/pages/new'
    },
    { 
      title: isRTL ? 'تحرير الصفحة الرئيسية' : 'Edit Home Page', 
      icon: FaEdit, 
      color: 'bg-green-600',
      href: '/admin/pages/home/builder'
    },
    { 
      title: isRTL ? 'معاينة الموقع' : 'Preview Site', 
      icon: FaEye, 
      color: 'bg-purple-600',
      href: '/'
    },
    { 
      title: isRTL ? 'إعدادات الموقع' : 'Site Settings', 
      icon: FaCog, 
      color: 'bg-orange-600',
      href: '/admin/settings'
    }
  ];

  return (
    <AdminLayout language={language} currentPage="dashboard">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isRTL ? 'لوحة التحكم' : 'Dashboard'}
            </h1>
            <p className="text-gray-600">
              {isRTL ? 'مرحباً بك في لوحة تحكم موقع مكة البركة' : 'Welcome to Mecca El Baraka Admin Panel'}
            </p>
          </div>
          <div className="flex gap-2">
            <button 
              className={`px-3 py-1 rounded ${language === 'ar' ? 'bg-blue-700 text-white' : 'bg-gray-200'}`} 
              onClick={() => setLanguage('ar')}
            >
              عربي
            </button>
            <button 
              className={`px-3 py-1 rounded ${language === 'en' ? 'bg-blue-700 text-white' : 'bg-gray-200'}`} 
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div key={index} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-sm text-green-600">{stat.change}</p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isRTL ? 'إجراءات سريعة' : 'Quick Actions'}
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {quickActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <a
                    key={index}
                    href={action.href}
                    className={`${action.color} text-white p-4 rounded-lg hover:opacity-90 transition-opacity`}
                  >
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                      <IconComponent className="w-5 h-5" />
                      <span className="font-medium">{action.title}</span>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent Pages */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isRTL ? 'الصفحات الحديثة' : 'Recent Pages'}
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentPages.map((page) => (
                <div key={page.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">{page.title}</h3>
                    <p className="text-sm text-gray-600">
                      {isRTL ? 'آخر تعديل:' : 'Last modified:'} {page.lastModified}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      page.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {page.status === 'active' ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive')}
                    </span>
                    <a
                      href={`/admin/pages/${page.id}/builder`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <FaEdit className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard; 